import { useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { AnimatePresence, motion, type TargetAndTransition } from "framer-motion";
import { Select } from "./Select";
import { useTemplates } from "../hooks/useTemplates";
import { useHomes } from "../hooks/useSettings";
import { CollapsePanel, listRow, stagger } from "../motion";
import { EmptyState } from "./EmptyState";
import { IconTodos } from "./icons";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export function TemplatePanel() {
  const { query, create, instantiate, remove } = useTemplates();
  const { data: homes } = useHomes();
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("other");
  const [recurrence, setRecurrence] = useState("none");
  const [recurrenceDay, setRecurrenceDay] = useState("");
  const [recurrenceWeekday, setRecurrenceWeekday] = useState("");
  const [tags, setTags] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const templates = query.data ?? [];
  const visibleTemplates = showAll ? templates : templates.slice(0, 3);

  function selectRecurrence(value: string) {
    setRecurrence(value);
    if (value === "weekly") setRecurrenceDay("");
    if (value === "monthly" || value === "yearly") setRecurrenceWeekday("");
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await create.mutateAsync({
        title: title.trim(),
        category,
        recurrence,
        recurrenceDay: recurrenceDay === "" ? null : Number(recurrenceDay),
        recurrenceWeekday: recurrenceWeekday === "" ? null : Number(recurrenceWeekday),
        tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
      });
      // Only reset + close on success — a failed save keeps the typed fields.
      setTitle(""); setCategory("other"); setRecurrence("none"); setRecurrenceDay(""); setRecurrenceWeekday(""); setTags(""); setShowForm(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to save template — try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="section">
      <div style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", marginBottom: "var(--space-3)" }}>
        <h2 className="section-title" style={{ margin: 0 }}>Templates</h2>
        <motion.button type="button" className="btn btn-secondary" onClick={() => setShowForm((s) => !s)} whileTap={{ scale: 0.97 }}>
          {showForm ? "Close" : "New template"}
        </motion.button>
      </div>

      <AnimatePresence initial={false}>
        {showForm ? (
          <CollapsePanel>
            <form className="form-card" onSubmit={submit}>
            <label htmlFor="tpl-title">Title</label>
            <input id="tpl-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Add new resident to database sheet" />
            <div className="form-row">
              <div>
                <label htmlFor="tpl-category">Category</label>
                <Select
                  id="tpl-category"
                  value={category}
                  onChange={setCategory}
                  options={[
                    { value: "admission", label: "admission" },
                    { value: "icp", label: "icp" },
                    { value: "monthly", label: "monthly" },
                    { value: "other", label: "other" },
                  ]}
                />
              </div>
              <div>
                <label htmlFor="tpl-recurrence">Recurrence</label>
                <Select
                  id="tpl-recurrence"
                  value={recurrence}
                  onChange={selectRecurrence}
                  options={[
                    { value: "none", label: "none" },
                    { value: "daily", label: "daily" },
                    { value: "weekly", label: "weekly" },
                    { value: "monthly", label: "monthly" },
                    { value: "yearly", label: "yearly" },
                  ]}
                />
              </div>
            </div>
            {recurrence === "weekly" ? (
              <div className="form-row">
                <div>
                  <label htmlFor="tpl-weekday">Weekday</label>
                  <Select
                    id="tpl-weekday"
                    value={recurrenceWeekday}
                    onChange={setRecurrenceWeekday}
                    placeholder="Choose…"
                    options={[
                      { value: "", label: "Choose…", disabled: true },
                      ...WEEKDAYS.map((label, i) => ({ value: String(i), label })),
                    ]}
                  />
                </div>
              </div>
            ) : recurrence === "monthly" || recurrence === "yearly" ? (
              <div className="form-row">
                <div>
                  <label htmlFor="tpl-day">Day of month</label>
                  <Select
                    id="tpl-day"
                    value={recurrenceDay}
                    onChange={setRecurrenceDay}
                    placeholder="Choose…"
                    options={[
                      { value: "", label: "Choose…", disabled: true },
                      ...DAYS.map((d) => ({ value: String(d), label: String(d) })),
                    ]}
                  />
                </div>
              </div>
            ) : null}
            <label htmlFor="tpl-tags">Tags (comma separated)</label>
            <input id="tpl-tags" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="admission, sheet" />
            {error ? <p className="error">{error}</p> : null}
          <div className="form-actions">
              <motion.button type="submit" className="btn btn-primary" disabled={submitting} whileTap={{ scale: 0.97 }}>Save template</motion.button>
              <motion.button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} whileTap={{ scale: 0.97 }}>Cancel</motion.button>
            </div>
            </form>
          </CollapsePanel>
        ) : null}
      </AnimatePresence>

      {query.isLoading ? <p className="muted">Loading…</p> : null}
      {!query.isLoading && (query.data ?? []).length === 0 ? (
        <EmptyState icon={<IconTodos />} title="No templates yet" />
      ) : null}

      <motion.ul className="todo-list" variants={stagger(0.04)} initial="hidden" animate="show">
        <AnimatePresence initial={false}>
          {visibleTemplates.map((tpl) => (
            <motion.li key={tpl.id} className="todo-row-item" variants={listRow} exit={listRow.exit as TargetAndTransition}>
              <div className="todo-row">
                <div className="todo-main">
                  <p className="todo-title">{tpl.title}</p>
                  <div className="todo-meta">
                    {tpl.category ? <span className="chip">{tpl.category}</span> : null}
                    {tpl.recurrence !== "none" ? <span className="chip">{tpl.recurrence}</span> : null}
                    {tpl.tags
                      .filter((t) => t !== tpl.category && t !== tpl.recurrence)
                      .map((t) => (
                        <span key={t} className="chip">{t}</span>
                      ))}
                  </div>
                </div>
                <motion.button type="button" className="btn btn-primary" style={{ minHeight: 40, padding: "0 var(--space-3)" }} onClick={() => instantiate.mutate({ id: tpl.id })} whileTap={{ scale: 0.96 }}>
                  Use now
                </motion.button>
                <motion.button type="button" className="btn btn-icon btn-ghost" aria-label="Delete template" onClick={() => remove.mutate(tpl.id)} whileTap={{ scale: 0.9 }}>
                  ✕
                </motion.button>
              </div>
            </motion.li>
          ))}
        </AnimatePresence>
      </motion.ul>
      {templates.length > 3 ? (
        <div style={{ marginTop: "var(--space-3)", textAlign: "center" }}>
          <motion.button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAll((s) => !s)} whileTap={{ scale: 0.97 }}>
            {showAll ? "Show less" : "Show more"}
          </motion.button>
        </div>
      ) : null}
      {homes && homes.length === 0 ? null : null}
    </section>
  );
}
